import { OrgOverrides } from 'custom/types';
import { resolveBaseOrgSlug, resolveOrgFromHostname } from './org';

type OrgOverrideLoader = () => Promise<{ default: OrgOverrides }>;
type Cache = { org: string, value: OrgOverrides | null };

let loaders: Record<string, OrgOverrideLoader> = {};
let cache: Cache | null = null;
let inflight: { org: string, promise: Promise<OrgOverrides | null> } | null = null;

export const registerOrgOverrideLoaders = (
  next: Record<string, OrgOverrideLoader>,
): void => {
  loaders = next;
};

// An exact registration wins; otherwise ondemand hosts fall back to the base
// org's overlay so `dadeschools-d942a6` gets the `dadeschools` chunk.
const resolveOverrideKey = (hostname: string): string => {
  const org = resolveOrgFromHostname(hostname);
  if (loaders[org]) return org;
  const base = resolveBaseOrgSlug(hostname);
  return loaders[base] ? base : org;
};

export const loadOrgOverrides = (
  hostname: string = window.location.hostname,
): Promise<OrgOverrides | null> => {
  const org = resolveOverrideKey(hostname);
  if (cache?.org === org) return Promise.resolve(cache.value);
  if (inflight?.org === org) return inflight.promise;

  const loader = loaders[org];
  if (!loader) {
    cache = { org, value: null };
    return Promise.resolve(null);
  }

  const promise = loader()
    .then((mod) => {
      cache = { org, value: mod.default };
      return mod.default;
    })
    .catch(() => {
      cache = { org, value: null };
      return null;
    })
    .finally(() => {
      if (inflight?.org === org) inflight = null;
    });

  inflight = { org, promise };
  return promise;
};

export const getOrgOverrides = (
  hostname: string = window.location.hostname,
): OrgOverrides | null => {
  const org = resolveOverrideKey(hostname);
  return cache?.org === org ? cache.value : null;
};

export const resetOrgOverridesCache = (): void => {
  cache = null;
  inflight = null;
};
